import { useState } from 'react';
import {
  Button,
  Checkbox,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconFileDescription,
  IconSend,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import { useSubmitPortalCreditApp } from '@/hooks/usePortalApi';
import type { PortalCreditApplication } from '@/types';

const BUSINESS_TYPE_OPTIONS = [
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'wholesale', label: 'Wholesale' },
  { value: 'retail', label: 'Retail' },
  { value: 'institution', label: 'Institution' },
  { value: 'school', label: 'School' },
];

export default function CreditApplication() {
  const submitCreditApp = useSubmitPortalCreditApp();
  const [submitted, setSubmitted] = useState(false);

  const form = useForm<PortalCreditApplication>({
    initialValues: {
      legal_business_name: '',
      dba_name: '',
      business_type: '',
      tax_id: '',
      years_in_business: undefined,
      annual_revenue: '',
      requested_credit_limit: undefined,
      bank_name: '',
      bank_contact_phone: '',
      bank_account_number: '',
      trade_ref_1_name: '',
      trade_ref_1_phone: '',
      trade_ref_1_account: '',
      trade_ref_2_name: '',
      trade_ref_2_phone: '',
      trade_ref_2_account: '',
      trade_ref_3_name: '',
      trade_ref_3_phone: '',
      trade_ref_3_account: '',
      authorized_signer_name: '',
      authorized_signer_title: '',
      agreed_to_terms: false,
    },
    validate: {
      legal_business_name: (v) =>
        v.trim().length === 0 ? 'Legal business name is required' : null,
      business_type: (v) =>
        !v ? 'Business type is required' : null,
      authorized_signer_name: (v) =>
        v.trim().length === 0 ? 'Authorized signer name is required' : null,
      agreed_to_terms: (v) =>
        v !== true ? 'You must agree to the terms to submit' : null,
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    submitCreditApp.mutate(values, {
      onSuccess: () => {
        notifications.show({
          title: 'Application Submitted',
          message:
            'Your credit application has been submitted. Our credit team will review your application within 1-2 business days.',
          color: 'green',
        });
        setSubmitted(true);
      },
      onError: () => {
        notifications.show({
          title: 'Error',
          message: 'Failed to submit credit application. Please try again.',
          color: 'red',
        });
      },
    });
  };

  if (submitted) {
    return (
      <Stack gap="lg">
        <PageHeader
          title="Apply for Credit"
          description="Submit a credit application for your business account"
        />
        <Paper p="xl" shadow="sm">
          <Stack align="center" gap="md" py="xl">
            <IconCheck size={48} color="var(--mantine-color-green-6)" />
            <Title order={3}>Application Submitted</Title>
            <Text c="dimmed" ta="center" maw={500}>
              Your credit application has been submitted. Our credit team will
              review your application within 1-2 business days.
            </Text>
          </Stack>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Apply for Credit"
        description="Submit a credit application for your business account"
      />

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="lg">
          {/* Section 1: Business Information */}
          <Paper p="lg" shadow="sm">
            <Stack gap="md">
              <Group gap="xs">
                <IconFileDescription size={20} />
                <Text fw={700} size="lg">
                  Business Information
                </Text>
              </Group>

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Legal Business Name"
                  placeholder="Enter legal business name"
                  required
                  {...form.getInputProps('legal_business_name')}
                />
                <TextInput
                  label="DBA Name"
                  placeholder="Doing business as (optional)"
                  {...form.getInputProps('dba_name')}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <Select
                  label="Business Type"
                  placeholder="Select business type"
                  data={BUSINESS_TYPE_OPTIONS}
                  required
                  {...form.getInputProps('business_type')}
                />
                <TextInput
                  label="Tax ID / EIN"
                  placeholder="XX-XXXXXXX"
                  {...form.getInputProps('tax_id')}
                />
              </SimpleGrid>

              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <NumberInput
                  label="Years in Business"
                  placeholder="e.g. 5"
                  min={0}
                  {...form.getInputProps('years_in_business')}
                />
                <TextInput
                  label="Annual Revenue"
                  placeholder="e.g. $1,000,000"
                  {...form.getInputProps('annual_revenue')}
                />
                <NumberInput
                  label="Requested Credit Limit"
                  placeholder="Enter amount"
                  prefix="$"
                  thousandSeparator=","
                  min={0}
                  {...form.getInputProps('requested_credit_limit')}
                />
              </SimpleGrid>
            </Stack>
          </Paper>

          {/* Section 2: Banking Information */}
          <Paper p="lg" shadow="sm">
            <Stack gap="md">
              <Text fw={700} size="lg">
                Banking Information
              </Text>

              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <TextInput
                  label="Bank Name"
                  placeholder="Enter bank name"
                  {...form.getInputProps('bank_name')}
                />
                <TextInput
                  label="Bank Contact Phone"
                  placeholder="(XXX) XXX-XXXX"
                  {...form.getInputProps('bank_contact_phone')}
                />
                <TextInput
                  label="Account Number"
                  placeholder="Enter account number"
                  {...form.getInputProps('bank_account_number')}
                />
              </SimpleGrid>
            </Stack>
          </Paper>

          {/* Section 3: Trade References */}
          <Paper p="lg" shadow="sm">
            <Stack gap="md">
              <Text fw={700} size="lg">
                Trade References
              </Text>

              <Text size="sm" c="dimmed">
                Reference 1
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <TextInput
                  label="Company Name"
                  placeholder="Enter company name"
                  {...form.getInputProps('trade_ref_1_name')}
                />
                <TextInput
                  label="Phone"
                  placeholder="(XXX) XXX-XXXX"
                  {...form.getInputProps('trade_ref_1_phone')}
                />
                <TextInput
                  label="Account #"
                  placeholder="Enter account number"
                  {...form.getInputProps('trade_ref_1_account')}
                />
              </SimpleGrid>

              <Text size="sm" c="dimmed">
                Reference 2
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <TextInput
                  label="Company Name"
                  placeholder="Enter company name"
                  {...form.getInputProps('trade_ref_2_name')}
                />
                <TextInput
                  label="Phone"
                  placeholder="(XXX) XXX-XXXX"
                  {...form.getInputProps('trade_ref_2_phone')}
                />
                <TextInput
                  label="Account #"
                  placeholder="Enter account number"
                  {...form.getInputProps('trade_ref_2_account')}
                />
              </SimpleGrid>

              <Text size="sm" c="dimmed">
                Reference 3
              </Text>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <TextInput
                  label="Company Name"
                  placeholder="Enter company name"
                  {...form.getInputProps('trade_ref_3_name')}
                />
                <TextInput
                  label="Phone"
                  placeholder="(XXX) XXX-XXXX"
                  {...form.getInputProps('trade_ref_3_phone')}
                />
                <TextInput
                  label="Account #"
                  placeholder="Enter account number"
                  {...form.getInputProps('trade_ref_3_account')}
                />
              </SimpleGrid>
            </Stack>
          </Paper>

          {/* Section 4: Authorization */}
          <Paper p="lg" shadow="sm">
            <Stack gap="md">
              <Text fw={700} size="lg">
                Authorization
              </Text>

              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                <TextInput
                  label="Authorized Signer Name"
                  placeholder="Enter full name"
                  required
                  {...form.getInputProps('authorized_signer_name')}
                />
                <TextInput
                  label="Title"
                  placeholder="e.g. Owner, CFO"
                  {...form.getInputProps('authorized_signer_title')}
                />
              </SimpleGrid>

              <Checkbox
                label="I authorize FreshOps to obtain credit information and verify the above information"
                {...form.getInputProps('agreed_to_terms', { type: 'checkbox' })}
              />
              {form.errors.agreed_to_terms && (
                <Text size="xs" c="red">
                  {form.errors.agreed_to_terms}
                </Text>
              )}

              <Group justify="flex-end" mt="md">
                <Button
                  type="submit"
                  leftSection={<IconSend size={16} />}
                  loading={submitCreditApp.isPending}
                >
                  Submit Application
                </Button>
              </Group>
            </Stack>
          </Paper>
        </Stack>
      </form>
    </Stack>
  );
}
